export interface RecordingDeletionProgress {
  phase: 'loading' | 'deleting' | 'finishing' | 'complete';
  deleted: number;
  total: number | null;
}
export interface RecordingFile {
  id: number;
  recording_url: string | null;
}

/** The storage API may already have cleared references before replying. */
export async function clearRecordingReferences(
  rows: RecordingFile[],
  writer: {
    clear: (row: RecordingFile) => Promise<void>;
    read: (rows: RecordingFile[]) => Promise<RecordingFile[]>;
  },
): Promise<number> {
  await Promise.all(rows.map((row) => writer.clear(row)));
  const current = new Map((await writer.read(rows)).map((row) => [row.id, row.recording_url]));
  return rows.filter((row) => !current.get(row.id)).length;
}

/** A retry queries the remaining files; retain the completed work in the dialog. */
export function combineDeletionProgress(
  previous: RecordingDeletionProgress | null,
  next: RecordingDeletionProgress,
): RecordingDeletionProgress {
  if (next.total === null) return previous ? { ...previous, phase: next.phase } : next;
  const total = Math.max(previous?.total ?? 0, next.total);
  return { ...next, total, deleted: total - next.total + next.deleted };
}

/** Count only batches whose files and call references have both been removed. */
export async function deleteRecordingBatches(
  rows: RecordingFile[],
  writer: {
    deleteFiles: (rows: RecordingFile[]) => Promise<void>;
    clearRows: (rows: RecordingFile[]) => Promise<number>;
  },
  onProgress?: (progress: RecordingDeletionProgress) => void,
  batchSize = 25,
): Promise<number> {
  if (!Number.isInteger(batchSize) || batchSize < 1) throw new Error('Invalid deletion batch size');
  let deleted = 0;
  onProgress?.({ phase: 'deleting', deleted, total: rows.length });
  for (let offset = 0; offset < rows.length; offset += batchSize) {
    const batch = rows.slice(offset, offset + batchSize);
    await writer.deleteFiles(batch);
    const cleared = await writer.clearRows(batch);
    deleted += cleared;
    onProgress?.({ phase: 'deleting', deleted, total: rows.length });
    if (cleared !== batch.length)
      throw new Error('Some recording details changed. Refresh and retry the remaining recordings.');
  }
  onProgress?.({ phase: 'finishing', deleted, total: rows.length });
  return deleted;
}
